import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";
const COMPANY = "Fractional Risk Group LLC";
const PRODUCT = "RiskSurvey.ai";
const CONTACT_EMAIL = "bill.raab@fractionalriskgroup.com";
const CONTACT_ADDRESS = "Fractional Risk Group LLC, Pennsylvania, USA";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 bg-navy rounded-md flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-navy text-base">{PRODUCT}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 md:p-12">

          <div className="mb-10">
            <h1 className="font-heading font-bold text-3xl text-navy mb-2">Privacy Policy</h1>
            <p className="text-sm text-slate-500">Effective Date: {EFFECTIVE_DATE}</p>
          </div>

          <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">

            <section>
              <p>
                {COMPANY} ("we," "us," or "our") operates the {PRODUCT} platform. This Privacy Policy explains how we collect, use, disclose, and protect information about users of our platform, including insurance carriers, their staff, and insureds who complete surveys. By using {PRODUCT}, you agree to the practices described in this policy.
              </p>
            </section>

            <Section title="1. Information We Collect">
              <p className="font-medium text-slate-800 mb-2">A. Information Carrier Users Provide</p>
              <ul className="list-disc pl-5 space-y-1 mb-4">
                <li>Account registration data: name, business email, company name, job title</li>
                <li>Carrier profile data: company name, logo, primary contact information</li>
                <li>Uploaded documents: underwriting guidelines, safety policies, AI training examples</li>
                <li>Inspection orders: insured names, property addresses, policy numbers</li>
                <li>Payment information (processed by our payment processor; we do not store raw card data)</li>
              </ul>

              <p className="font-medium text-slate-800 mb-2">B. Information Insured Users Provide</p>
              <ul className="list-disc pl-5 space-y-1 mb-4">
                <li>Survey responses submitted through the AI chat interface or survey form</li>
                <li>Photos uploaded during an inspection (property images, equipment, documentation)</li>
                <li>Property address and related property details</li>
              </ul>

              <p className="font-medium text-slate-800 mb-2">C. Information We Collect Automatically</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Log data: IP addresses, browser type, pages visited, timestamps, referring URLs</li>
                <li>Usage data: features accessed, inspection volume, session durations</li>
                <li>Device information: operating system, screen resolution</li>
              </ul>
            </Section>

            <Section title="2. How We Use Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-2">
                <li>Provide, operate, and maintain the {PRODUCT} platform</li>
                <li>Process inspections, generate AI findings, and produce reports</li>
                <li>Authenticate users and maintain account security</li>
                <li>Send transactional communications (inspection notifications, password resets, report completions)</li>
                <li>Process payments and manage billing</li>
                <li>Improve our AI models using aggregated, de-identified data</li>
                <li>Detect, prevent, and investigate fraud or security incidents</li>
                <li>Comply with legal obligations and respond to lawful requests</li>
                <li>Send service updates and, where permitted, marketing communications to Carrier Users (you may opt out at any time)</li>
              </ul>
            </Section>

            <Section title="3. Property Data Pre-Fill">
              <p>
                The platform may automatically retrieve publicly available property data (including FEMA flood zone information via the National Flood Hazard Layer API) to pre-populate inspection fields and reduce repetitive questioning. This data is sourced from public government databases and is used solely to improve survey accuracy and efficiency. No fee is charged for this data retrieval, and it does not involve sharing your information with third parties for their own commercial purposes.
              </p>
              <p className="mt-3">
                With your permission, the platform may also retrieve property data from third-party data providers (such as ATTOM Data Solutions). When activated, such data is subject to the third party's own terms and privacy practices. We will inform you before enabling any paid third-party data sources.
              </p>
            </Section>

            <Section title="4. How We Share Information">
              <p>We do not sell your personal information. We may share information in the following circumstances:</p>

              <p className="font-medium text-slate-800 mt-4 mb-2">A. Within the Platform (Carrier-Insured Relationship)</p>
              <p>
                Inspection data, survey responses, photos, findings, and reports are visible to authorized staff of the Carrier that ordered the inspection. Insured Users' data is accessible only to the Carrier they submitted it to.
              </p>

              <p className="font-medium text-slate-800 mt-4 mb-2">B. Service Providers</p>
              <p>We share data with trusted third-party vendors who help us operate the platform, including:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Cloud infrastructure and storage providers</li>
                <li>AI and machine learning API providers (for processing inspection conversations and photos)</li>
                <li>Email delivery services (for transactional notifications)</li>
                <li>Payment processors</li>
                <li>Analytics and error-monitoring tools</li>
              </ul>
              <p className="mt-2">All service providers are contractually required to protect your data and use it only as directed by us.</p>

              <p className="font-medium text-slate-800 mt-4 mb-2">C. Legal Requirements</p>
              <p>
                We may disclose information if required by law, court order, regulatory authority, or when we believe disclosure is necessary to protect the rights, safety, or property of {COMPANY}, our users, or the public.
              </p>

              <p className="font-medium text-slate-800 mt-4 mb-2">D. Business Transfers</p>
              <p>
                In connection with a merger, acquisition, or sale of assets, your information may be transferred. We will notify affected users prior to any such transfer.
              </p>
            </Section>

            <Section title="5. Data Retention">
              <p>
                We retain Carrier account data and inspection records for the duration of the carrier agreement plus five (5) years, unless a longer retention period is required by law or agreed upon in writing.
              </p>
              <p className="mt-3">
                Insured Users' survey data and photos are retained for the same period as the associated carrier's data. Insured Users may request deletion of their data by contacting the Carrier that ordered their inspection; the Carrier is responsible for submitting deletion requests to us.
              </p>
              <p className="mt-3">
                Log and usage data is retained for up to twenty-four (24) months.
              </p>
            </Section>

            <Section title="6. Data Security">
              <p>
                We implement appropriate technical and organizational security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Encryption of data in transit (TLS 1.2+) and at rest</li>
                <li>Role-based access controls limiting data access to authorized personnel</li>
                <li>Carrier-level data isolation (each carrier's data is logically separated)</li>
                <li>API rate limiting and authentication token expiration</li>
                <li>Audit logging of all administrative and data access events</li>
              </ul>
              <p className="mt-3">
                Despite these measures, no transmission over the internet is completely secure. You acknowledge and accept this inherent risk.
              </p>
            </Section>

            <Section title="7. Cookies and Tracking">
              <p>
                The platform uses session cookies and local storage to maintain authenticated sessions and remember user preferences. We do not use third-party advertising cookies or cross-site tracking technologies. You may configure your browser to refuse cookies, but this may affect platform functionality.
              </p>
            </Section>

            <Section title="8. Your Rights and Choices">
              <p>Depending on your jurisdiction, you may have rights including:</p>
              <ul className="list-disc pl-5 mt-2 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Deletion:</strong> Request deletion of your data, subject to legal retention requirements.</li>
                <li><strong>Portability:</strong> Request an export of your inspection data in a machine-readable format.</li>
                <li><strong>Objection:</strong> Object to certain processing activities, including direct marketing.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>. We will respond within thirty (30) days. Carrier Users may also manage their data directly within the platform's account settings.
              </p>
            </Section>

            <Section title="9. California Privacy Rights (CCPA)">
              <p>
                If you are a California resident, you have the right to know what personal information we collect and how it is used, the right to delete your personal information, the right to opt out of the "sale" of personal information (we do not sell personal information), and the right to non-discrimination for exercising your privacy rights. To exercise these rights, contact us at {CONTACT_EMAIL}.
              </p>
            </Section>

            <Section title="10. Children's Privacy">
              <p>
                The platform is intended for use by businesses and their adult employees or policyholders. We do not knowingly collect personal information from individuals under the age of 18. If you believe we have collected information from a minor, please contact us immediately at {CONTACT_EMAIL}.
              </p>
            </Section>

            <Section title="11. Third-Party Links">
              <p>
                The platform may contain links to third-party websites or services. This Privacy Policy does not apply to those third parties. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </Section>

            <Section title="12. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify registered Carrier Users of material changes via email or platform notification at least thirty (30) days before the changes take effect. The "Effective Date" at the top of this page indicates when the policy was last updated.
              </p>
            </Section>

            <Section title="13. Contact Us">
              <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
              <address className="mt-2 not-italic text-slate-700">
                <strong>{COMPANY}</strong><br />
                Attn: Bill Raab — Privacy Officer<br />
                {CONTACT_ADDRESS}<br />
                Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a><br />
                Phone: 814-592-5824
              </address>
            </Section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between text-xs text-slate-400">
        <span>© 2026 {PRODUCT}. All rights reserved.</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-navy transition-colors">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-base text-navy mb-3 pb-2 border-b border-slate-100">{title}</h2>
      {children}
    </section>
  );
}
